import type { ShiftOpeningRequest, ShiftOpeningResponse } from "@/api/user/shiftOpening"

export interface OpeningScope { shopId: number; userId: number }
interface StorageLike { getItem: (key: string) => string | null; setItem: (key: string, value: string) => void; removeItem: (key: string) => void }
export class OpeningRejectedError extends Error {}

export function openingScopeKey(scope: OpeningScope) {
  return `shift-opening:v1:${scope.shopId}:${scope.userId}`
}

export function createOpeningSession(storage: StorageLike, scope: OpeningScope) {
  if (!Number.isSafeInteger(scope.shopId) || scope.shopId <= 0 || !Number.isSafeInteger(scope.userId) || scope.userId <= 0) {
    throw new Error("门店或员工身份尚未就绪")
  }
  const key = openingScopeKey(scope)
  return {
    pending(): ShiftOpeningRequest | null {
      const raw = storage.getItem(key)
      if (!raw) return null
      const parsed = JSON.parse(raw) as ShiftOpeningRequest
      if (!parsed.requestId || typeof parsed.carryPrevious !== "boolean" || !Number.isSafeInteger(parsed.countedCash) || !Number.isSafeInteger(parsed.cashMovement)) {
        throw new Error("未完成的开班请求记录异常，请联系管理员核对")
      }
      return parsed
    },
    async submit(input: ShiftOpeningRequest, send: (data: ShiftOpeningRequest) => Promise<{ code: number; msg?: string; data?: ShiftOpeningResponse }>) {
      const payload = this.pending() ?? input
      // Persist before dispatch; storage failure must prevent an untrackable write.
      storage.setItem(key, JSON.stringify(payload))
      const result = await send(payload)
      if (typeof result.code === "number" && result.code !== 0 && result.code !== -1) {
        storage.removeItem(key)
        throw new OpeningRejectedError(result.msg || "开班请求被拒绝")
      }
      const data = result.data
      if (result.code !== 0 || !data?.recordId || data.requestId !== payload.requestId || data.shopId !== scope.shopId || data.openerUserId !== scope.userId || data.status == null || ![0, 1, 2].includes(data.status)) {
        throw new Error("开班结果尚未确认，请使用原请求重试核对")
      }
      storage.removeItem(key)
      return data
    },
  }
}

export function openingCashDifference(counted: number, expected: number | null | undefined, movement: number) {
  return expected == null ? null : counted - expected - movement
}
